import React, { useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { Link } from "react-router-dom";
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Select from "react-select";

const EditJobRole = ({ show, onClose, jobroleId, onUpdated }) => {
    const [jobRoleName, setJobRoleName] = useState('');
    const baseURL = process.env.REACT_APP_API_BASE_URL;
    const [description, setDescription] = useState('');
    const [errors, setErrors] = useState({});
    const [nameLengthError, setNameLengthError] = useState('');
    const [descLengthError, setDescLengthError] = useState('');

    const [deptOpt, setDeptOpt] = useState([]);
    const [selDept, setSelDept] = useState(null);
    const [selectedDeptOpt, setSelDeptOpt] = useState(null);

    const [subdeptOpt, setSubDeptOpt] = useState([]);
    const [selSubDept, setSelSubDept] = useState(null);
    const [selectedSubDeptOpt, setSelSubDeptOpt] = useState(null);

    const [levelOPT, setLevelOpt] = useState([]);
    const [selLev, setSelLev] = useState(null);
    const [selLEvOPt, setSelLevOpt] = useState(null);

    /** ✅ Generic fetch helper to reduce repetition */
    const fetchOptions = async (url, labelKey, valueKey = "sno") => {
        const { data } = await axios.get(url);
        const list = data.data || data; // handles both {data:[...]} or [...]
        return list.map((item) => ({
            value: item[valueKey],
            label: item[labelKey],
        }));
    };

    /** ✅ Fetch Departments */
    useEffect(() => {
        fetchOptions(`${baseURL}/dropdown/get_department`, "department_name")
            .then(setDeptOpt)
            .catch((err) => console.error("Error fetching departments:", err));
    }, [baseURL]);

    /** ✅ Fetch Sub-Departments (when Department changes) */
    useEffect(() => {
        if (!selDept) return;
        fetchOptions(`${baseURL}/sub_department/get_depts/${selDept}`, "sub_department_name")
            .then(setSubDeptOpt)
            .catch((err) => console.error("Error fetching sub-departments:", err));
    }, [baseURL, selDept]);

    /** ✅ Fetch Levels */
    useEffect(() => {
        fetchOptions(`${baseURL}/level/get_active`, "level_name")
            .then(setLevelOpt)
            .catch((err) => console.error("Error fetching levels:", err));
    }, [baseURL]);

    /** ✅ Fetch Job Role (when editing) */
    useEffect(() => {
        const fetchJobRole = async () => {
            try {
                const { data } = await axios.get(`${baseURL}/jobrole/${jobroleId}`);
                const job = data.data || {};

                setJobRoleName(job.jobrole_name || "");
                setDescription(job.description || "");

                // Department
                const dept = deptOpt.find((d) => d.value == job.department_id);
                setSelDeptOpt(dept || null);
                setSelDept(dept?.value || null);

                // Sub-department
                if (dept?.value) {
                    const subList = await fetchOptions(
                        `${baseURL}/sub_department/get_depts/${dept.value}`,
                        "sub_department_name"
                    );
                    setSubDeptOpt(subList);
                    const subd = subList.find((s) => s.value == job.sub_department_id);
                    setSelSubDeptOpt(subd || null);
                    setSelSubDept(subd?.value || null);
                }

                // Level
                const level = levelOPT.find((l) => l.value == job.level_id);
                setSelLevOpt(level || null);
                setSelLev(level?.value || null);
            } catch (err) {
                console.error("Error fetching job role:", err);
            }
        };

        if (show && jobroleId && deptOpt.length && levelOPT.length) {
            fetchJobRole();
        }
    }, [show, jobroleId, baseURL, deptOpt, levelOPT]);



    const handleNameChange = (e) => {
        let value = e.target.value || '';
        value = value.charAt(0).toUpperCase() + value.slice(1); // Capitalize first letter
        setJobRoleName(value);

        if (value.length <= 300) {
            setNameLengthError('');
        }
    };

    const handleDescriptionChange = (e) => {
        let value = e.target.value || '';
        value = value.charAt(0).toUpperCase() + value.slice(1); // Capitalize first letter
        setDescription(value);

        if (value.length <= 500) {
            setDescLengthError('');
        }
    };

    const handleSubmit = () => {
        console.log("Submit clicked");

        setErrors({});
        setNameLengthError('');
        setDescLengthError('');

        const safeName = jobRoleName ?? '';
        const safeDescription = description ?? '';


        if (!selDept) {
            setErrors(prev => ({
                ...prev,
                dept: "This field is required"
            }));
            return;
        }

        if (!selSubDept) {
            setErrors(prev => ({
                ...prev,
                subdept: "This field is required"
            }));
            return;
        }

        if (!selLev) {
            setErrors(prev => ({
                ...prev,
                level: "This field is required"
            }));
            return;
        }

        if (safeName.trim().length === 0) {
            setErrors(prev => ({
                ...prev,
                jobrole_name: "This field is required"
            }));
            return;
        }

        if (safeName.length > 300) {
            setNameLengthError('Limit reached (max 300 characters)');
            return;
        }

        if (safeDescription.length > 500) {
            setDescLengthError('Limit reached (max 500 characters)');
            return;
        }

        axios.post(`${baseURL}/jobrole/${jobroleId}`, {
            department_id: selDept,
            sub_department_id: selSubDept,
            level_id: selLev,
            jobrole_name: safeName,
            description: safeDescription,
        })
            .then(response => {
                console.log("API response:", response.data);
                toast.success("Job Role updated successfully!");
                onClose();
                if (onUpdated) onUpdated();
            })
            .catch(error => {
                console.error("Update failed:", error);
                if (error.response?.status === 400 && error.response.data.errors) {
                    setErrors(error.response.data.errors);
                }
            });
    };


    return (
        <Modal show={show} size="md">
            <div className="modal-content">
                <div className="modal-header" style={{ backgroundColor: '#3ab384' }}>
                    <h3 className="modal-title text-white">Update Job Role</h3>
                    <Button variant="" type="button" className="close px-6 py-2" data-dismiss="modal" onClick={onClose}>
                        <span className='text-white'>×</span>
                    </Button>
                </div>
                <div className="modal-body">
                    <div className="row">
                        <div className="col-lg-12 mb-2">
                            <label className="text-black font-w500 fs-7">Department <span className="required">*</span></label>
                            <Select placeholder="Select Department"
                                options={deptOpt}
                                value={selectedDeptOpt}
                                onChange={(e) => {
                                    setSelDept(e.value);
                                    setSelDeptOpt(e);
                                }}
                            />
                            {errors.dept && (
                                <div className="text-danger">{errors.dept}</div>
                            )}
                        </div>

                        <div className="col-lg-12 mb-2">
                            <label className="text-black font-w500 fs-7">Sub Department <span className="required">*</span></label>
                            <Select placeholder="Select Sub Department"
                                options={subdeptOpt}
                                value={selectedSubDeptOpt}
                                onChange={(e) => {
                                    setSelSubDept(e.value);
                                    setSelSubDeptOpt(e);
                                }}
                            />
                            {errors.subdept && (
                                <div className="text-danger">{errors.subdept}</div>
                            )}
                        </div>

                        <div className="col-lg-12 mb-2">
                            <label className="text-black font-w500 fs-7">Level <span className="required">*</span></label>
                            <Select placeholder="Select Level"
                                options={levelOPT}
                                value={selLEvOPt}
                                onChange={(e) => {
                                    setSelLev(e.value);
                                    setSelLevOpt(e);
                                }}
                            />
                            {errors.level && (
                                <div className="text-danger">{errors.level}</div>
                            )}
                        </div>

                        <div className="col-lg-12 mb-2">
                            <label className="text-black font-w500 fs-7">
                                Job Role <span className="required">*</span>
                            </label>
                            <input
                                type="text"
                                className={`form-control ${errors.jobrole_name ? 'is-invalid' : ''}`}
                                placeholder="Enter Job Role"
                                value={jobRoleName}
                                onChange={handleNameChange}
                            />
                            {errors.jobrole_name && (
                                <div className="invalid-feedback">{errors.jobrole_name}</div>
                            )}
                            {nameLengthError && (
                                <span className="text-danger">{nameLengthError}</span>
                            )}
                        </div>

                        <div className="col-lg-12 mb-2">
                            <label className="text-black font-w500 fs-7">Description</label>
                            <textarea
                                rows={4}
                                className={`form-control ${errors.description ? 'is-invalid' : ''}`}
                                placeholder="Description"
                                value={description}
                                onChange={handleDescriptionChange}
                            />
                            {descLengthError && (
                                <span className="text-danger">{descLengthError}</span>
                            )}
                        </div>
                    </div>
                    <div className="d-flex justify-content-center gap-3 mt-4 mb-4">
                        <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
                        <Button variant="primary" size="sm" onClick={handleSubmit}>Update Job Role</Button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default EditJobRole;
